import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

class PlayerImageComponent extends React.Component {
  constructor(props) {
    super(props);

    this.ZoomForCurrentTime = this.ZoomForCurrentTime.bind(this);
    this.applyZoomPreview = this.applyZoomPreview.bind(this);
    this.clearZoomPreview = this.clearZoomPreview.bind(this);
  }

  componentDidMount() {
    if (!this.props.isPlaying) {
      this.updateZoomPreview();
    }
  }

  componentDidUpdate(prevProps) {
    const isPlayEnded = prevProps.isPlaying && !this.props.isPlaying;
    const isSeeked = this.props.runningState.get("seekToken") !== prevProps.runningState.get("seekToken");
    const isIdleTimeChanged = !this.props.isPlaying && prevProps.playhead !== this.props.playhead;
    const isPlayerLoaded = !prevProps.isLoaded && this.props.isLoaded;

    // Check if property component changed from SMART_FOCUS to another panel
    const SMART_FOCUS_COMPONENTS = ["SMART_FOCUS", "SMART_FOCUS_MOBILE"];
    const wasSmartFocus = SMART_FOCUS_COMPONENTS.includes(prevProps.propertyComponent);
    const isSmartFocus = SMART_FOCUS_COMPONENTS.includes(this.props.propertyComponent);
    const propertyComponentChanged = wasSmartFocus && !isSmartFocus;

    if (isPlayEnded || isSeeked || isIdleTimeChanged || isPlayerLoaded || propertyComponentChanged) {
      this.updateZoomPreview();
    }

    // Clear zoom preview when playing starts
    if (!prevProps.isPlaying && this.props.isPlaying) {
      this.clearZoomPreview(this.props.item);
    }
  }

  componentWillUnmount() {
    this.clearZoomPreview(this.props.item);
  }

  updateZoomPreview() {
    const { playhead, playStart, item } = this.props;
    const SMART_FOCUS_COMPONENTS = [
      "SMART_FOCUS",
      "SMART_FOCUS_MOBILE",
    ];

    if (!this.props.isPlaying) {
      // Calculate the "media time" for the image based on playhead position
      // For images, media time is simply the time elapsed since the item started
      const currentTime = playhead - playStart;

      if (!SMART_FOCUS_COMPONENTS.includes(this.props.propertyComponent) || this.props.isMobileView) {
        this.ZoomForCurrentTime(currentTime);
      } else {
        this.clearZoomPreview(item);
      }
    }
  }

  ZoomForCurrentTime(currentTime) {
    try {
      const { item } = this.props;
      if (!item || !item.get) return;

      // zoomData is stored on the workspace item (Immutable.Map)
      const zoomDataImmutable = item.get("zoomData");
      if (!zoomDataImmutable) {
        this.clearZoomPreview(item);
        return;
      }
      const zoomData = zoomDataImmutable.toJS ? zoomDataImmutable.toJS() : zoomDataImmutable;
      if (!zoomData || Object.keys(zoomData).length === 0) {
        this.clearZoomPreview(item);
        return;
      }

      // Find the active zoom segment for current time
      const activeSegment = Object.values(zoomData).reduce((found, zoomGroup) => {
        if (found || !zoomGroup || typeof zoomGroup !== "object") return found;

        const { zoomIn, zoomOut } = zoomGroup;
        const start = zoomIn?.start;
        const end = zoomOut?.end;

        if (typeof start !== "number" || typeof end !== "number") return found;
        if (currentTime < start || currentTime > end) return found;

        // Determine which segment to use based on currentTime
        if (zoomOut?.start !== undefined && zoomOut?.end !== undefined &&
          currentTime >= zoomOut.start && currentTime <= zoomOut.end) {
          return zoomOut;
        }
        if (zoomIn) {
          return zoomIn;
        }
        return found;
      }, null);

      if (activeSegment) {
        this.applyZoomPreview(item, activeSegment);
      } else {
        this.clearZoomPreview(item);
      }
    } catch (e) {
      // Silently fail for preview errors
    }
  }

  clearZoomPreview(item) {
    try {
      const wsItem = (item?.get) ? item : this.props.item;
      if (!wsItem?.get) return;

      const id = wsItem.get("id");
      if (!id) return;

      const overlayDiv = document.getElementById(`${id}animo`);
      if (!overlayDiv) return;

      overlayDiv.style.transform = "";
      overlayDiv.style.transformOrigin = "";

      // Reset the parent container overflow to hidden (default for cropped items)
      const itemContainer = document.getElementById(id);
      if (itemContainer) {
        itemContainer.style.overflow = "";
      }
    } catch (e) {
      // Silently fail
    }
  }

  applyZoomPreview(item, segment) {
    try {
      if (!segment) return;

      const wsItem = (item?.get) ? item : this.props.item;
      const { projectData, zoomFactor = 1 } = this.props;

      if (!wsItem?.get || !projectData?.get) return;

      const id = wsItem.get("id");
      if (!id) return;

      const overlayDiv = document.getElementById(`${id}animo`);
      if (!overlayDiv) return;

      const itemContainer = document.getElementById(id);
      if (itemContainer) {
        itemContainer.style.overflow = "visible";
      }

      const projectWidth = projectData.get("width");
      const projectHeight = projectData.get("height");
      if (!Number.isFinite(projectWidth) || !Number.isFinite(projectHeight)) return;

      const validZoomFactor = Number.isFinite(zoomFactor) ? zoomFactor : 1;
      const itemX = wsItem.get("x") || 0;
      const itemY = wsItem.get("y") || 0;
      const zoomW = segment.width || projectWidth;
      const zoomH = segment.height || projectHeight;
      const zoomX = segment.x || 0;
      const zoomY = segment.y || 0;

      // Replicate the transform math used in zoomPanEffect for a static frame
      const dx = zoomX - itemX;
      const dy = zoomY - itemY;
      const scaledProjWidth = projectWidth * validZoomFactor;
      const scaledProjHeight = projectHeight * validZoomFactor;
      const zScaleX = scaledProjWidth / (zoomW * validZoomFactor);
      const zScaleY = scaledProjHeight / (zoomH * validZoomFactor);
      const correctedWidth = dx * validZoomFactor;
      const correctedHeight = dy * validZoomFactor;
      const zX = zScaleX * correctedWidth * -1;
      const zY = zScaleY * correctedHeight * -1;
      const tweenX = zX - (itemX * validZoomFactor);
      const tweenY = zY - (itemY * validZoomFactor);

      overlayDiv.style.transformOrigin = "0px 0px";
      overlayDiv.style.transform = `translate(${tweenX}px, ${tweenY}px) scale(${zScaleX}, ${zScaleY})`;
    } catch (e) {
      // Silently fail
    }
  }

  render() {
    // This component doesn't render anything - it just manages the zoom preview
    // The actual image is rendered by the parent component (workspace-item.jsx)
    return this.props.children || null;
  }
}

PlayerImageComponent.propTypes = {
  playStart: PropTypes.number,
  playhead: PropTypes.number,
  item: PropTypes.object,
  children: PropTypes.node,
  runningState: PropTypes.object,
  isLoaded: PropTypes.bool,
  isPlaying: PropTypes.bool,
  projectData: PropTypes.object,
  zoomFactor: PropTypes.number,
  propertyComponent: PropTypes.string,
  isMobileView: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  runningState: state.app.get("runningState"),
  playhead: state.app.get("playhead"),
  isPlaying: state.app.get("isPlayAll"),
  isLoaded: state.app.get("isLoaded"),
  projectData: state.projectDetails,
  zoomFactor: state.app.get("zoomFactor"),
  propertyComponent: state.app.getIn(["propertyWindow", "component"]),
  isMobileView: state.app.get("isMobileView"),
});

const mapDispatchToProps = () => ({});

const PlayerImage = connect(
  mapStateToProps,
  mapDispatchToProps
)(PlayerImageComponent);

export default PlayerImage;
